import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { loginError, microsoftRedirectUri, resumeLoginReturn, safeLoginReturnPath, validateLoginSession } from '../services/loginSession';

function Login({ onLoginSuccess, returnTo = null, microsoftOnly = false, prePasswordSession = false }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [msLoading, setMsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleMicrosoftCallback = useCallback(async (code, state) => {
        setMsLoading(true);
        setError(null);
        try {
            const msalFlow = sessionStorage.getItem('msal_auth_flow');
            let flowObj = null;
            if (msalFlow) {
                flowObj = JSON.parse(msalFlow);
                sessionStorage.removeItem('msal_auth_flow');
            }

            const payload = {
                code,
                redirect_uri: flowObj?.redirect_uri || microsoftRedirectUri()
            };

            if (state) payload.state = state;
            if (flowObj) payload.auth_flow = flowObj;

            // The React dev proxy rewrites Origin. Microsoft needs the browser
            // origin for the existing SPA token exchange, so keep it direct.
            const baseUrl = window.location.port === '3000' ? 'http://localhost:5002' : '';
            const response = await fetch(`${baseUrl}/api/auth/microsoft/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                const token = data.token;
                await validateLoginSession(token);
                window.history.replaceState({}, document.title,
                    window.location.pathname === '/login' ? '/' : window.location.pathname);
                localStorage.setItem('agent_token', token);
                if (!resumeLoginReturn()) onLoginSuccess(token);
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.error || 'Error al validar sesión de Microsoft. Es posible que el usuario no esté dado de alta como agente.');
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } catch (err) {
            setError(loginError(err.message));
            console.error('MS Callback error:', err);
            window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
            setMsLoading(false);
        }
    }, [onLoginSuccess]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const errorParam = params.get('error');
        const errorDesc = params.get('error_description');

        if (code) {
            handleMicrosoftCallback(code, state);
        } else if (errorParam) {
            setError(`Error de Microsoft: ${errorDesc || errorParam}`);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [handleMicrosoftCallback]);

    async function handleMicrosoftLogin() {
        setMsLoading(true);
        setError(null);
        try {
            const redirectUri = microsoftRedirectUri(window.location, returnTo);
            const queryParams = new URLSearchParams({ redirect_uri: redirectUri });
            const baseUrl = window.location.port === '3000' ? 'http://localhost:5002' : '';
            const response = await fetch(`${baseUrl}/api/auth/microsoft/login?${queryParams.toString()}`);

            if (response.ok) {
                const data = await response.json();
                const authUri = data.auth_url || data.auth_uri || data.flow?.auth_uri;
                if (!authUri || !data.flow) throw new Error('Microsoft no ha devuelto una dirección válida para iniciar sesión.');
                sessionStorage.setItem('msal_auth_flow', JSON.stringify(data.flow));
                sessionStorage.removeItem('msal_return_to');
                const target = safeLoginReturnPath(returnTo);
                if (target) sessionStorage.setItem('msal_return_to', target);
                window.location.href = authUri;
            } else {
                setError('No se pudo obtener la URL de autenticación de Microsoft.');
            }
        } catch (err) {
            setError(`Error de conexión con el servidor: ${err.message}`);
            console.error('MS Login error:', err);
        } finally {
            setMsLoading(false);
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (microsoftOnly) return;
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://oezimzeqmtmncuipqgwd.supabase.co/functions/v1/initiateauthcommand', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (response.ok) {
                const data = await response.json();
                let token = data.token;
                if (prePasswordSession) {
                    if (typeof token !== 'string' || !token.trim()) throw new Error('El servicio de autenticación no ha devuelto una sesión válida.');
                    const session = await fetch('/api/auth/password/session', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const result = await session.json().catch(() => ({}));
                    if (!session.ok) throw new Error(result.error || result.message || 'No se pudo iniciar la sesión de pre.');
                    token = result.token;
                }
                await validateLoginSession(token);
                if (window.location.pathname === '/login') window.history.replaceState({}, document.title, '/');
                localStorage.setItem('agent_token', token);
                onLoginSuccess(token);
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(loginError(errorData.message || errorData.error || 'Error de autenticación. Verifique sus credenciales.'));
            }
        } catch (err) {
            setError(loginError(err.message));
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#020817] p-4">
            <div className="w-full max-w-md space-y-8 bg-slate-900/40 p-10 rounded-2xl border border-slate-800 shadow-2xl backdrop-blur-sm">
                <div className="text-center">
                    <img
                        src="https://imaginaenergia.com/wp-content/themes/uncode/assets/images/imagina_energia_logo.svg"
                        alt="Imagina Energía"
                        className="h-12 w-auto mx-auto mb-6"
                    />
                    <h2 className="text-3xl font-bold text-white tracking-tight">Acceso Agentes</h2>
                    <p className="mt-2 text-slate-400">Introduce tus credenciales para comenzar</p>
                </div>

                <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
                    {microsoftOnly && error && <p role="alert" className="text-red-500">{error}</p>}
                    {!microsoftOnly && <>
                    <div className="space-y-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                                <Mail size={18} />
                            </div>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-lg bg-slate-950 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                                placeholder="Email del agente"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                                <Lock size={18} />
                            </div>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-lg bg-slate-950 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                                placeholder="Contraseña"
                            />
                        </div>
                    </div>

                    {error && (
                        <div role="alert" className="flex items-center gap-2 text-red-500 bg-red-500/10 p-3 rounded-lg border border-red-500/20 text-sm">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || msLoading}
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin mr-2" />
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </button>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-slate-800"></span>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-[#020817] px-2 text-slate-500 font-mono tracking-widest">o bien</span>
                        </div>
                    </div>

                    </>}
                    <button
                        type="button"
                        onClick={handleMicrosoftLogin}
                        disabled={loading || msLoading}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white hover:bg-slate-100 text-slate-900 rounded-lg font-semibold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                        {msLoading ? (
                            <Loader2 className="animate-spin" size={20} />
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 23 23">
                                    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                                    <path fill="#f35325" d="M1 1h10v10H1z" />
                                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                                </svg>
                                <span>Iniciar Sesión con Microsoft</span>
                            </>
                        )}
                    </button>

                    <div className="text-center pt-4">
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">
                            Acceso Empleados via Microsoft Entra
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Login;
